<template>
  <v-dialog
    v-model="isOpen"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    max-width="640px"
    scrollable>
    <v-card v-if="account">
      <v-card-title class="d-flex align-center">
        <v-avatar v-if="launcherLogo" size="24" class="mr-2" tile>
          <v-img :src="launcherLogo" contain />
        </v-avatar>
        <div style="min-width: 0">
          <div class="text-truncate">{{ account.username }}</div>
          <div class="caption text--secondary">
            {{ $t('sharedAccounts.manageAccount') }}
          </div>
        </div>
        <v-spacer />
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <!-- Account-level ban (admin / manager / employee). A full-account ban,
           distinct from the per-game bans in the list below. -->
      <div class="px-4 pt-2 pb-1">
        <v-alert v-if="account.banned" type="error" text dense class="mb-2">
          {{ $t('sharedAccounts.accountBan.description') }}
        </v-alert>
        <div class="d-flex align-center">
          <v-icon
            small
            :color="account.banned ? 'error' : 'success'"
            class="mr-2">
            {{ account.banned ? 'mdi-cancel' : 'mdi-check-circle-outline' }}
          </v-icon>
          <span
            class="text-body-2"
            :class="account.banned ? 'error--text' : 'success--text'">
            {{
              account.banned
                ? $t('sharedAccounts.accountBan.banned')
                : $t('sharedAccounts.accountBan.notBanned')
            }}
          </span>
          <v-spacer />
          <v-btn
            v-if="account.banned"
            small
            text
            color="primary"
            @click="requestBan(false)">
            {{ $t('sharedAccounts.accountBan.remove') }}
          </v-btn>
          <v-btn v-else small text color="error" @click="requestBan(true)">
            <v-icon small left>mdi-cancel</v-icon>
            {{ $t('sharedAccounts.accountBan.set') }}
          </v-btn>
        </div>
      </div>

      <v-divider class="mt-1" />

      <div class="px-4 pt-3 pb-2">
        <div class="caption text--secondary mb-2">
          {{
            $tc('sharedAccounts.gamesCount', totalGames, { count: totalGames })
          }}<template v-if="bannedCount">
            ·
            <span class="error--text">{{
              $t('sharedAccounts.bannedCount', { count: bannedCount })
            }}</span></template
          >
        </div>
        <div v-if="account.banned" class="caption text--disabled mb-2">
          {{ $t('sharedAccounts.accountBan.gamesDisabled') }}
        </div>
        <v-text-field
          v-model="search"
          :placeholder="$t('sharedAccounts.gamesDialog.search')"
          prepend-inner-icon="mdi-magnify"
          clearable
          dense
          outlined
          hide-details />
      </div>

      <v-divider />

      <v-card-text style="max-height: 420px" class="pa-0">
        <v-list v-if="filteredGames.length" two-line class="py-0">
          <template v-for="(game, i) in filteredGames">
            <v-list-item
              :key="game.game_id"
              :disabled="account.banned"
              @click="$emit('edit-ban', { account, game })">
              <v-list-item-content>
                <v-list-item-title>{{ game.name }}</v-list-item-title>
                <v-list-item-subtitle v-if="isBanned(game)" class="error--text">
                  {{ $t('sharedAccounts.bannedUntil') }}
                  {{ formatDate(new Date(game.banned_until), false) }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-else class="success--text">
                  {{ $t('sharedAccounts.available') }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="!account.banned">
                <v-btn v-if="isBanned(game)" small text color="error">
                  <v-icon x-small left>mdi-pencil</v-icon>
                  {{ $t('sharedAccounts.banDialog.editBan') }}
                </v-btn>
                <v-btn v-else small text color="primary">
                  <v-icon x-small left>mdi-cancel</v-icon>
                  {{ $t('sharedAccounts.banDialog.setBan') }}
                </v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-divider
              v-if="i < filteredGames.length - 1"
              :key="game.game_id + '-d'" />
          </template>
        </v-list>
        <p v-else class="text-center text--secondary py-8 mb-0">
          {{ $t('components.noData.noEntriesFound') }}
        </p>
      </v-card-text>

      <template v-if="canManage">
        <v-divider />
        <v-card-actions class="px-4 py-3">
          <v-btn text color="error" @click="$emit('delete', { account })">
            <v-icon left>mdi-delete</v-icon>
            {{ $t('sharedAccounts.delete.title') }}
          </v-btn>
          <v-spacer />
          <v-btn text color="primary" @click="$emit('edit', { account })">
            <v-icon left>mdi-pencil</v-icon>
            {{ $t('sharedAccounts.edit.title') }}
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import formatDate from '@/mixins/format-date'
import { isGameActivelyBanned } from '@/utils/shared-accounts'

export default {
  name: 'AccountGamesDialog',
  mixins: [formatDate],
  props: {
    value: { type: Boolean, default: false },
    account: { type: Object, default: null },
    launcherLogo: { type: String, default: '' },
    // Admin-only: shows the Edit / Delete actions for the account.
    canManage: { type: Boolean, default: false },
  },
  data() {
    return {
      search: '',
    }
  },
  computed: {
    isOpen: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    games() {
      return this.account && Array.isArray(this.account.games)
        ? this.account.games
        : []
    },
    totalGames() {
      return this.games.length
    },
    bannedCount() {
      return this.games.filter((g) => this.isBanned(g)).length
    },
    filteredGames() {
      const term = (this.search || '').trim().toLowerCase()
      const list = term
        ? this.games.filter((g) => (g.name || '').toLowerCase().includes(term))
        : this.games.slice()
      // Banned first, then alphabetical - surface the actionable ones.
      return list.sort((a, b) => {
        const ba = this.isBanned(a) ? 0 : 1
        const bb = this.isBanned(b) ? 0 : 1
        if (ba !== bb) return ba - bb
        return (a.name || '').localeCompare(b.name || '')
      })
    },
  },
  watch: {
    value(open) {
      if (open) this.search = ''
    },
  },
  methods: {
    isBanned(game) {
      return isGameActivelyBanned(game)
    },
    requestBan(banned) {
      // Parent owns the styled confirm + API call (like the per-game ban flow).
      this.$emit('request-ban', { account: this.account, banned })
    },
    close() {
      this.isOpen = false
    },
  },
}
</script>
